/**
 * planKitchenStaging — pure placement for a staged kitchen.
 *
 * Pure: no I/O, no Firestore, no React, no THREE. Same shape as the bathroom
 * planner (`../index.js`): mint props in PLACEMENT order, feed each one
 * forward as an obstacle to the next, then spend the cap at the END by
 * declared precedence (`../budget.js`).
 *
 * Placement order is not rank order, for the bathroom's reason: a prop that
 * must sit somewhere specific loses every race against one that will go
 * anywhere. The crock is placed before the boards so it hugs the range and the
 * boards stand beside it; the soap before the herb pot that joins it.
 *
 * Kitchen rules (Megan, 2026-10-18; research in the review artifact):
 *   - at most 8 props, and at least 70% of the counter length bare;
 *   - the PREP ZONE between sink and range stays clear, full depth;
 *   - nothing on a sink or a cooktop;
 *   - nothing passes into an upper cabinet or a hood (depth-aware);
 *   - the tea towel hangs on a PROCEDURAL range's handle only; with a catalog
 *     range it is a folded dish towel beside the sink.
 */

const { createMinter } = require('../minter');
const { ART_TYPES } = require('../attributes');
const { trimToBudget } = require('../budget');
const { stagingPropDimsM } = require('../../propDims');
const {
  KITCHEN_PROP_PROGRAM,
  MAX_PROPS_PER_KITCHEN,
  MAX_COUNTER_SHARE,
  MAX_RUN_SHARE,
  KITCHEN_NEAR_REACH_M,
  LEAN_MIN_RUN_M,
  CENTREPIECE_MIN_DEPTH_M,
  ISLAND_GROUP_FROM_END,
  GROUP_GAP_M,
  ART_STEAM_CLEARANCE_M,
  KITCHEN_ART_REACH_M,
} = require('./program');
const {
  COUNTER_TYPES,
  readKitchen,
  counterCorners,
  counterStretches,
  runPosition,
  nearestEnd,
  closestOnRun,
  nearCounter,
  planDistance,
  planGapToObject,
} = require('./geometry');
const { prepZone, groupingSpacing, nearBlocks } = require('./keepOuts');
const { rankWallsByFreeRun, hangOnWall } = require('../walls');
const { ART_CENTRE_HEIGHT_M } = require('../constants');
const {
  extractWallEndpoints,
} = require('../../../bathroom-analyzer/extraction');
const {
  resolveKitchenRegion,
} = require('../../../kitchen-assembly/kitchenRegion');
const { isPointInsidePolygon } = require('../../../geometry/pointInPolygon');
const {
  placeOnCounter,
  placeOnRangeHandle,
  clearanceFrom,
  feasibleSpans,
} = require('./placements');
const { objectWorldFromLocal } = require('../../../dorm-styling/plan/shared');
const { FRONT_SETBACK_M, planDims, isBasinLike } = require('../surfaces');
const { objectTypeOf } = require('../../ownership');

const IN = 0.0254;
/**
 * The order props are PLACED in. Rank (precedence) is the programme's order.
 * Props that must stand somewhere SPECIFIC go first — the soap beside the
 * sink, the crock and boards at the range, the towel on its handle — then the
 * ones that can go anywhere. Placed fruit-first, the bowl and the tulips took
 * the counter beside the sink on every kitchen without an island and the soap
 * landed in 7 of 23 corpus kitchens with a sink.
 */
const PLACEMENT_ORDER = [
  'staging-dish-soap-set',
  'staging-utensil-crock',
  'staging-mills',
  'staging-cutting-boards',
  'staging-tea-towel',
  // Fruit first: it outranks the flowers, so in a tight kitchen it must not
  // lose its spot to them. In a corner it keeps the square free for the vase.
  'staging-fruit-bowl',
  'staging-vase-tulips',
  'staging-herb-pot',
  'staging-cookbook-stand',
  'staging-canisters',
  // Last: the print takes whatever stretch of backsplash the kit leaves.
  'staging-art-leaned',
];

/** Props that hang rather than stand: they take no counter length. */
const HUNG = new Set(['staging-tea-towel', 'staging-art']);
/**
 * What kitchen art keeps its distance from: steam and splashes. Every basin
 * `isBasinLike` knows, so the hung piece and the leaned print (which measures
 * from `kitchen.sinks`) keep clear of the same sinks.
 */
const COOKING_TYPES = new Set(['range', 'stove', 'cooktop', 'oven']);
const isSteamSource = (o) =>
  COOKING_TYPES.has(objectTypeOf(o)) || isBasinLike(o);

function planKitchenStaging(room, style = {}, opts = {}) {
  if (!room) return [];
  const program = KITCHEN_PROP_PROGRAM;
  // Kitchen art carries the pack's KITCHEN subject (citrus, herbs), in the
  // same palette as its bathroom art: one house, room-appropriate pictures.
  const kitchenStyle = style?.kitchen?.art
    ? { ...style, art: style.kitchen.art }
    : style;
  const { mint, curation, drop } = createMinter(
    room,
    kitchenStyle,
    opts,
    program
  );
  const kitchen = readKitchen(room);
  const entries = [];
  const scan = () => [...(room.objects || []), ...entries];
  const counters = kitchen.counters;
  const wallRuns = counters.filter((c) => !c.isIsland);
  const islands = counters
    .filter((c) => c.isIsland && c.host.dimensions.z >= CENTREPIECE_MIN_DEPTH_M)
    .sort((a, b) => b.hostW - a.hostW);
  const prepOf = new Map(counters.map((c) => [c, prepZone(c, kitchen)]));
  const stretchOf = counterStretches(counters);

  /** What other groupings a new prop keeps its distance from: the props out, and the stove. */
  const spacingBlocks = (c, type, depth) =>
    groupingSpacing(
      c,
      [
        ...entries.filter((e) => !HUNG.has(e.type)),
        ...kitchen.ranges,
        ...kitchen.cooktops,
      ],
      type,
      depth
    );
  /**
   * An OPEN run end keeps the front edge's setback (4 in), not the 2 in a
   * surface end gets: a cookbook 2 in from a counter end that faces a doorway
   * is at an exposed edge.
   */
  const openEndBlocks = (c) =>
    c.openEnds.map((sx) =>
      sx < 0
        ? { start: 0, end: FRONT_SETBACK_M }
        : { start: c.hostW - FRONT_SETBACK_M, end: c.hostW }
    );
  /** What a prop must stay off: the prep zone, open ends, and — when `spaced` — the other groupings. */
  const blockedFor = (c, type, spaced, depth) => [
    ...prepOf.get(c),
    ...openEndBlocks(c),
    ...(spaced ? spacingBlocks(c, type, depth) : []),
  ];
  const placedOf = (type) => entries.find((e) => e.type === type) || null;
  const counterOf = (entry) =>
    entry
      ? counters.find((c) => c.obj.id === entry.parents?.[0]?.id) || null
      : null;

  /** Counter length already carrying props, per counter. */
  const usedOn = (c) =>
    entries
      .filter((e) => !HUNG.has(e.type) && e.parents?.[0]?.id === c.obj.id)
      .reduce((sum, e) => sum + (e.dimensions?.x || 0), 0);
  /**
   * Would this prop take the counter past its own share? Asked of the props
   * with no anchor only: the crock and boards at a short range landing are one
   * grouping that belongs there, while fruit and flowers that pile onto it are
   * what left Golden West's other three runs bare. A BARE counter is never
   * crowded by one grouping: capped, the 22 in fruit-and-flowers pair could
   * not take a bare 43 in run and squeezed in beside a soap set instead
   * (Downstairs FB14AF05).
   */
  const crowds = (c, wish, alsoM = 0) =>
    usedOn(c) > 0 &&
    usedOn(c) + alsoM + (stagingPropDimsM(wish.type)?.x || 0) >
      MAX_RUN_SHARE * c.hostW + 1e-9;

  /**
   * Try counters in order with the given options; first that seats the prop
   * wins. Every counter is tried spaced from the other groupings first; an
   * essential prop may then stand closer, an optional one is left out rather
   * than crowd a grouping (stagers' "less is more").
   */
  const tryCounters = (wish, list, optsFor) => {
    for (const spaced of wish.optional ? [true] : [true, false]) {
      for (const c of list) {
        const o = optsFor(c);
        if (!o) continue;
        const placed = placeOnCounter(
          c,
          wish,
          scan(),
          (w, fields) => mint(w, w.type, fields),
          {
            ...o,
            blocked: [
              ...blockedFor(c, wish.type, spaced, o.depth),
              ...(o.extraBlocked || []),
            ],
          }
        );
        if (placed) return placed;
      }
    }
    return null;
  };

  /**
   * A prop with no anchor of its own goes where the kitchen is BAREST: of
   * every counter's feasible spots, the one farthest from the nearest prop
   * already out. Placed first-fit down a list, these piled onto whichever run
   * came first; spread, they balance the room the way a stager works it, one
   * small grouping per stretch of counter.
   */
  const spreadAcross = (wish, list, optsFor) => {
    const out = entries.filter((e) => !HUNG.has(e.type)).map((e) => e.center);
    if (!out.length) return tryCounters(wish, byLengthDesc(list), optsFor);
    let best = null;
    for (const c of list) {
      const o = optsFor(c);
      if (!o || crowds(c, wish)) continue;
      const fields = placeOnCounter(c, wish, scan(), (w, f) => f, {
        ...o,
        blocked: [
          ...blockedFor(c, wish.type, true, o.depth),
          ...(o.extraBlocked || []),
        ],
        farFrom: out,
      });
      if (!fields) continue;
      const score = clearanceFrom(fields.center, out);
      if (!best || score > best.score) best = { fields, score };
    }
    return best ? mint(wish, wish.type, best.fields) : null;
  };

  /**
   * How deep a SHOW piece sits: mid-depth on a counter with no wall behind it
   * (an island or peninsula has no back to tuck toward, and both long edges
   * are open), forward of the uppers on a wall run.
   */
  const showDepth = (c) => (c.wallBacked ? 'front-half' : 'centre');
  /** A prop that belongs at the back, on a counter that may have no back. */
  const backDepth = (c, depth) => (c.wallBacked ? depth : 'centre');

  const corners = counterCorners(counters);
  /** Plan points of the props already out, the hung towel aside. */
  const placedPoints = () =>
    entries.filter((e) => !HUNG.has(e.type)).map((e) => e.center);
  /** Which side of the first piece of the grouping the second goes (+1 up the run), and at what depth. */
  let groupSide = null;
  let groupDepth = null;
  /** A corner square kept for the second piece: {counter, t, reach, depth}. */
  let groupSlot = null;

  /** Run position of the middle of the longest stretch this prop could stand on. */
  const stretchMid = (c, wish, depth, spaced) => {
    const spans = feasibleSpans(
      c,
      stagingPropDimsM(wish.type),
      scan(),
      blockedFor(c, wish.type, spaced, depth),
      depth
    );
    const s = spans.sort((a, b) => b.hi - b.lo - (a.hi - a.lo))[0];
    return s ? (s.lo + s.hi) / 2 : null;
  };
  const pointOn = (c, t) => {
    const p = objectWorldFromLocal(c.host, t - c.hostW / 2, 0);
    return { x: p.x, z: p.z };
  };
  /** Barest first: farthest from the nearest prop already out. */
  const barestFirst = (list, pointOf) => {
    const out = placedPoints();
    if (!out.length) return list;
    return list
      .map((x) => ({ x, score: clearanceFrom(pointOf(x), out) }))
      .sort((a, b) => b.score - a.score)
      .map((r) => r.x);
  };

  /** Where a prop would stand on `c` (no entry minted), with `extra` as obstacles. */
  const dryRun = (c, type, extra, opts, spaced) =>
    placeOnCounter(c, { type }, [...scan(), ...extra], (w, f) => f, {
      blocked: blockedFor(c, type, spaced, opts.depth),
      ...opts,
    });
  /** Options seating a piece beside `fields` on `c`, on side `side`. */
  const besideOpts = (c, fields, type, side, depth) => ({
    depth,
    towardT:
      runPosition(c, fields.center) +
      side *
        ((fields.dimensions.x + (stagingPropDimsM(type)?.x || 0)) / 2 +
          GROUP_GAP_M),
    maxFromT: KITCHEN_NEAR_REACH_M.fruit,
  });

  /**
   * The spots the fruit-and-flowers grouping may take, best first — where
   * stagers put it:
   *   1. an island, a third of the way from an end (away from its sink);
   *   2. the CORNER of an L — the square too deep to work in, where a stager
   *      angles a vase (Apartment Therapy, "kitchen counter corner"; Megan's
   *      reference photos) — the TALLER piece in the square;
   *   3. the barest counter run, the PAIR centred on its free stretch.
   * Each is { c, depth, firstOpts, side } (the second piece goes on `side`
   * of the first), or for a corner whose square is the partner's,
   * { c, depth, partnerOpts, side } (the first goes on `side` of it).
   */
  const groupSpots = (wish, partnerType, spaced) => {
    const partnerW = stagingPropDimsM(partnerType)?.x || 0;
    const tallerPartner =
      (stagingPropDimsM(partnerType)?.y || 0) >
      (stagingPropDimsM(wish.type)?.y || 0);
    const spots = [];
    for (const c of islands) {
      const third = ISLAND_GROUP_FROM_END * c.hostW;
      const sinkT =
        sinkCounter?.counter === c
          ? runPosition(c, sinkCounter.sink.center)
          : null;
      const marks =
        sinkT == null
          ? [third, c.hostW - third]
          : sinkT > c.hostW / 2
            ? [third]
            : [c.hostW - third];
      for (const t of marks)
        spots.push({
          c,
          depth: 'centre',
          firstOpts: { depth: 'centre', towardT: t, maxFromT: c.hostW / 6 },
          side: t < c.hostW / 2 ? 1 : -1,
        });
    }
    for (const k of barestFirst(corners, (k) => k.point)) {
      const c = k.counter;
      if (crowds(c, wish, partnerW)) continue;
      const inward = k.t < c.hostW / 2 ? 1 : -1;
      for (const depth of c.wallBacked
        ? ['centre', 'front-half']
        : ['centre']) {
        const inCorner = { depth, towardT: k.t, maxFromT: k.size / 2 };
        spots.push(
          tallerPartner
            ? { c, depth, partnerOpts: inCorner, side: inward }
            : { c, depth, firstOpts: inCorner, side: inward }
        );
      }
    }
    const runs = wallRuns
      .filter((c) => !crowds(c, wish, partnerW))
      .map((c) => ({ c, mid: stretchMid(c, wish, showDepth(c), spaced) }))
      .filter((r) => r.mid != null);
    const ordered = placedPoints().length
      ? barestFirst(runs, (r) => pointOn(r.c, r.mid))
      : runs.sort((a, b) => b.c.hostW - a.c.hostW);
    // Off the mark by half the partner, so the PAIR is centred.
    for (const { c, mid } of ordered)
      spots.push({
        c,
        depth: showDepth(c),
        firstOpts: {
          depth: showDepth(c),
          towardT: mid + (partnerW + GROUP_GAP_M) / 2,
        },
        side: -1,
      });
    return spots;
  };

  /** Seat both pieces at a spot (dry): {first, partner} fields, or null. */
  const seatPair = (spot, wish, partnerType, spaced) => {
    const { c, depth, side } = spot;
    if (spot.partnerOpts) {
      const partner = dryRun(c, partnerType, [], spot.partnerOpts, spaced);
      const first =
        partner &&
        dryRun(
          c,
          wish.type,
          [partner],
          besideOpts(c, partner, wish.type, side, depth),
          spaced
        );
      return first ? { first, partner } : null;
    }
    const first = dryRun(c, wish.type, [], spot.firstOpts, spaced);
    if (!first) return null;
    for (const s of [side, -side]) {
      const partner = dryRun(
        c,
        partnerType,
        [first],
        besideOpts(c, first, partnerType, s, depth),
        spaced
      );
      if (partner) return { first, partner };
    }
    return null;
  };

  /**
   * The first piece of the grouping: at the best spot where BOTH pieces fit,
   * and it keeps the second's seat in `groupSlot`. Only when no spot takes the
   * pair does it stand alone, at the best spot that takes it at all — a tall
   * vase that clears no upper must not cost the kitchen its fruit.
   *
   * Spacing from the other groupings is asked first, at every step, and given
   * up only when nothing else seats the piece: in a kitchen this tight a bowl
   * near the crock is still better than no bowl.
   */
  const placeGroupAnchor = (wish, partnerType) => {
    const spotsFor = {
      true: groupSpots(wish, partnerType, true),
      false: groupSpots(wish, partnerType, false),
    };
    for (const spaced of [true, false]) {
      for (const spot of spotsFor[spaced]) {
        const pair = seatPair(spot, wish, partnerType, spaced);
        if (!pair) continue;
        groupSlot = {
          counter: spot.c,
          depth: spot.depth,
          towardT: runPosition(spot.c, pair.partner.center),
          maxFromT: 0.5 * IN,
        };
        return mint(wish, wish.type, pair.first);
      }
    }
    for (const spaced of [true, false]) {
      for (const spot of spotsFor[spaced]) {
        const first =
          spot.firstOpts &&
          dryRun(spot.c, wish.type, [], spot.firstOpts, spaced);
        if (first) {
          groupSide = spot.side;
          groupDepth = spot.depth;
          return mint(wish, wish.type, first);
        }
      }
    }
    return null;
  };

  /** The second piece of the grouping, beside the first on the side `groupSide` chose. */
  const placeBesideGroup = (wish, anchor) => {
    const c = counterOf(anchor);
    if (!c) return null;
    if (groupSlot?.counter === c) {
      const { counter, ...slot } = groupSlot;
      const placed = tryCounters(wish, [counter], () => slot);
      if (placed) return placed;
    }
    const w = stagingPropDimsM(wish.type)?.x || 0;
    const at = runPosition(c, anchor.center);
    const step = (anchor.dimensions.x + w) / 2 + GROUP_GAP_M;
    const sides = groupSide ? [groupSide, -groupSide] : [1, -1];
    const depths = [...new Set([groupDepth || showDepth(c), showDepth(c)])];
    for (const depth of depths) {
      for (const side of sides) {
        const placed = tryCounters(wish, [c], () => ({
          depth,
          towardT: at + side * step,
          maxFromT: KITCHEN_NEAR_REACH_M.fruit,
        }));
        if (placed) return placed;
      }
    }
    return null;
  };

  /** Half a sink's larger plan side: how far its rim reaches from its centre. */
  const sinkReach = (sink) =>
    Math.max(planDims(sink).x || 0, planDims(sink).z || 0) / 2;

  // The counter with the sink, and where along it the sink is.
  const sinkCounter = (() => {
    for (const s of kitchen.sinks) {
      const c = counters.find((x) => nearCounter(x, s, 0.02));
      if (c) return { counter: c, sink: s };
    }
    return null;
  })();
  const rangeLandings = () =>
    kitchen.ranges
      .flatMap((r) =>
        wallRuns
          .filter((c) => nearCounter(c, r, 2 * IN))
          .map((c) => ({
            counter: c,
            range: r,
            end: closestOnRun(c, r.center),
          }))
      )
      // The SHORTER stretch first: the crock and mills take the landing that
      // leaves the most open counter for the other groupings (50 Park: the
      // 26 in end cap, not the 30 in slab that runs on into the peninsula top),
      // then the nearer end.
      .sort(
        (a, b) =>
          (stretchOf.get(a.counter) || 0) - (stretchOf.get(b.counter) || 0) ||
          planDistance(a.end.point, a.range.center) -
            planDistance(b.end.point, b.range.center)
      );
  const byLengthDesc = (list) => [...list].sort((a, b) => b.hostW - a.hostW);

  /**
   * The hung fallback for kitchen art: the bathroom's framed piece on an open
   * wall of the KITCHEN (an open-plan room's living walls are not its to
   * dress), centred at 58 in, refused within 24 in of a range, cooktop or
   * sink. Minted against the leaned print's programme line, so it keeps that
   * rank, as the folded dish towel keeps the tea towel's.
   */
  const hangKitchenArt = (wish) => {
    // A room typed kitchen with nothing of a kitchen in it (a fireplace and
    // ceiling lights) gets no kitchen art: there is no kitchen to hang it in.
    const hasFixtures =
      kitchen.counters.length ||
      kitchen.sinks.length ||
      kitchen.ranges.length ||
      kitchen.cooktops.length;
    if (!hasFixtures) return null;
    const endpoints = extractWallEndpoints(room) || [];
    const endpointsById = new Map(endpoints.map((e) => [e.id, e]));
    // Every cell of the region: a kitchen can be split across several.
    const regions = resolveKitchenRegion(room)?.polygons || [];
    const inKitchen = ({ axes, span }) => {
      if (!regions.length) return true;
      const t = (span.start + span.end) / 2;
      const p = {
        x: axes.start.x + axes.wallDir.x * t,
        z: axes.start.z + axes.wallDir.z * t,
      };
      // The wall's own line is the region's edge: ask a point either side of it.
      const n = { x: -axes.wallDir.z * 0.3, z: axes.wallDir.x * 0.3 };
      return [1, -1].some((sd) =>
        regions.some((region) =>
          isPointInsidePolygon({ x: p.x + sd * n.x, z: p.z + sd * n.z }, region)
        )
      );
    };
    const candidates = rankWallsByFreeRun(
      room,
      room.walls || [],
      endpointsById
    ).filter(inKitchen);
    // Near the kitchen itself, not merely inside its region: see
    // KITCHEN_ART_REACH_M.
    const fixtures = [
      ...kitchen.counters.map((c) => c.obj),
      ...kitchen.sinks,
      ...kitchen.ranges,
      ...kitchen.cooktops,
      ...kitchen.fridges,
    ];
    const nearKitchen = ({ center }) =>
      fixtures.some((o) => planGapToObject(center, o) <= KITCHEN_ART_REACH_M);
    return hangOnWall(
      room,
      candidates,
      'staging-art',
      ART_CENTRE_HEIGHT_M,
      (fields) => mint(wish, 'staging-art', fields),
      new Set(),
      {
        forbidOverTypes: isSteamSource,
        forbidClearanceM: ART_STEAM_CLEARANCE_M,
        // An OPEN wall: never directly over a counter, where it would hang
        // into the boards or the cookbook leaning on the backsplash below.
        refuseOverTypes: COUNTER_TYPES,
        accept: nearKitchen,
      }
    );
  };

  const stages = {
    'staging-fruit-bowl': (wish) =>
      placeGroupAnchor(wish, 'staging-vase-tulips'),

    // The flowers join the fruit or are left out: one grouping, never split
    // across the kitchen. Only with no fruit out do they take a spot of their own.
    'staging-vase-tulips': (wish) => {
      const fruit = placedOf('staging-fruit-bowl');
      return fruit
        ? placeBesideGroup(wish, fruit)
        : placeGroupAnchor(wish, 'staging-fruit-bowl');
    },

    'staging-dish-soap-set': (wish) => {
      if (!sinkCounter) return null;
      const { counter, sink } = sinkCounter;
      return tryCounters(wish, [counter], (c) => ({
        depth: 'back',
        towardT: runPosition(c, sink.center),
        maxFromT: sinkReach(sink) + KITCHEN_NEAR_REACH_M.sink,
      }));
    },

    'staging-utensil-crock': (wish) => {
      const millsW = stagingPropDimsM('staging-mills')?.x || 0;
      const crockOpts = (counter, end) => ({
        depth: backDepth(counter, 'back-third'),
        // At a slab end the crock hugs the range. Beside a range that runs
        // along the slab, the crock-and-mills pair is CENTRED on it: the
        // crock half a mill off the mark, the mills beside it.
        towardT: end.atEnd ? end.t : end.t - (millsW + GROUP_GAP_M) / 2,
        maxFromT: KITCHEN_NEAR_REACH_M.range,
      });
      // The crock and mills are one grouping: first a landing where the mills
      // fit BESIDE the crock (Pittsburgh's 15 in slab took the crock alone and
      // sent the mills to the range's other side), then any landing.
      const millsFit = (counter, crock) => {
        const crockT = runPosition(counter, crock.center);
        const step = (crock.dimensions.x + millsW) / 2 + GROUP_GAP_M;
        return [1, -1].some((side) =>
          dryRun(
            counter,
            'staging-mills',
            [crock],
            {
              depth: backDepth(counter, 'back-third'),
              towardT: crockT + side * step,
              maxFromT: KITCHEN_NEAR_REACH_M.crock,
            },
            true
          )
        );
      };
      const landings = rangeLandings();
      for (const { counter, end } of landings) {
        const crock = dryRun(
          counter,
          wish.type,
          [],
          crockOpts(counter, end),
          true
        );
        if (crock && millsFit(counter, crock)) {
          const placed = tryCounters(wish, [counter], () =>
            crockOpts(counter, end)
          );
          if (placed) return placed;
        }
      }
      for (const { counter, end } of landings) {
        const placed = tryCounters(wish, [counter], () =>
          crockOpts(counter, end)
        );
        if (placed) return placed;
      }
      return null;
    },

    'staging-cutting-boards': (wish) => {
      const leanable = wallRuns.filter(
        (c) => c.wallBacked && c.hostW >= LEAN_MIN_RUN_M
      );
      // Flank the range: the boards on the landing the crock did NOT take.
      const crockOn = counterOf(placedOf('staging-utensil-crock'));
      const landings = rangeLandings();
      const flanked = [
        ...landings.filter((l) => l.counter !== crockOn),
        ...landings.filter((l) => l.counter === crockOn),
      ];
      for (const { counter, end } of flanked) {
        if (!leanable.includes(counter)) continue;
        const placed = tryCounters(wish, [counter], () => ({
          depth: 'against-back',
          towardT: end.t,
        }));
        if (placed) return placed;
      }
      const sinkT = sinkCounter
        ? (c) => runPosition(c, sinkCounter.sink.center)
        : () => NaN;
      return tryCounters(wish, byLengthDesc(leanable), (c) => ({
        depth: 'against-back',
        awayFromT: sinkT(c),
      }));
    },

    'staging-tea-towel': (wish) => {
      for (const range of kitchen.hangableRanges) {
        const placed = placeOnRangeHandle(range, wish, (w, fields) =>
          mint(w, w.type, fields)
        );
        if (placed) return placed;
      }
      // A catalog range (or none we can hang from): a folded dish towel beside
      // the sink, at the tea towel's rank.
      // Minted against the TEA TOWEL's programme line, so it keeps that rank.
      if (!sinkCounter) return null;
      const { counter, sink } = sinkCounter;
      const towel = { ...wish, type: 'staging-dish-towel-folded' };
      for (const spaced of [true, false]) {
        const placed = placeOnCounter(
          counter,
          towel,
          scan(),
          (w, fields) => mint(wish, w.type, fields),
          {
            blocked: blockedFor(
              counter,
              towel.type,
              spaced,
              showDepth(counter)
            ),
            depth: showDepth(counter),
            towardT: runPosition(counter, sink.center),
            maxFromT: sinkReach(sink) + KITCHEN_NEAR_REACH_M.sink,
          }
        );
        if (placed) return placed;
      }
      return null;
    },

    'staging-herb-pot': (wish) => {
      const soap = placedOf('staging-dish-soap-set');
      const host = counterOf(soap);
      if (!host) return null;
      return tryCounters(wish, [host], (c) => ({
        depth: 'back',
        towardT: runPosition(c, soap.center),
        maxFromT:
          (soap.dimensions.x + stagingPropDimsM(wish.type).x) / 2 +
          KITCHEN_NEAR_REACH_M.soap,
      }));
    },

    'staging-mills': (wish) => {
      const crock = placedOf('staging-utensil-crock');
      const host = counterOf(crock);
      if (host) {
        // Beside the crock, on the side toward the middle of the counter —
        // never out toward an open end.
        const crockT = runPosition(host, crock.center);
        const step =
          (crock.dimensions.x + stagingPropDimsM(wish.type).x) / 2 +
          GROUP_GAP_M;
        const inward = Math.sign(host.hostW / 2 - crockT) || 1;
        for (const side of [inward, -inward]) {
          const beside = tryCounters(wish, [host], (c) => ({
            depth: backDepth(c, 'back-third'),
            towardT: crockT + side * step,
            maxFromT: KITCHEN_NEAR_REACH_M.crock,
          }));
          if (beside) return beside;
        }
      }
      for (const { counter, end } of rangeLandings()) {
        const placed = tryCounters(wish, [counter], (c) => ({
          depth: backDepth(c, 'back-third'),
          towardT: end.t,
          maxFromT: KITCHEN_NEAR_REACH_M.range,
        }));
        if (placed) return placed;
      }
      const fruit = placedOf('staging-fruit-bowl');
      const fruitHost = counterOf(fruit);
      if (!fruitHost) return null;
      return tryCounters(wish, [fruitHost], (c) => ({
        depth: showDepth(c),
        towardT: runPosition(c, fruit.center),
        maxFromT:
          (fruit.dimensions.x + stagingPropDimsM(wish.type).x) / 2 +
          KITCHEN_NEAR_REACH_M.fruit,
      }));
    },

    'staging-cookbook-stand': (wish) => {
      return spreadAcross(
        wish,
        wallRuns.filter((c) => c.wallBacked),
        () => ({ depth: 'against-back' })
      );
    },

    'staging-art-leaned': (wish) => {
      // Leaned on a backsplash, 24 in clear of steam, where the kitchen is
      // barest; else hung on an open kitchen wall.
      const steam = [...kitchen.ranges, ...kitchen.cooktops, ...kitchen.sinks];
      const leaned = spreadAcross(
        wish,
        wallRuns.filter((c) => c.wallBacked),
        (c) => ({
          depth: 'against-back',
          extraBlocked: nearBlocks(
            c,
            steam,
            wish.type,
            'against-back',
            ART_STEAM_CLEARANCE_M
          ),
        })
      );
      return leaned || hangKitchenArt(wish);
    },

    'staging-canisters': (wish) => {
      const leanable = wallRuns.filter((c) => c.wallBacked);
      const fridge = kitchen.fridges[0];
      if (fridge) {
        const ordered = [...leanable].sort(
          (a, b) =>
            planDistance(nearestEnd(a, fridge.center).point, fridge.center) -
            planDistance(nearestEnd(b, fridge.center).point, fridge.center)
        );
        // Flush to the backsplash: the jars stand in a row against it.
        return tryCounters(wish, ordered, (c) => ({
          depth: 'against-back',
          towardT: nearestEnd(c, fridge.center).t,
        }));
      }
      return tryCounters(wish, byLengthDesc(leanable), () => ({
        depth: 'against-back',
      }));
    },
  };

  const lineOf = new Map(program.map((p) => [p.type, p]));
  const wasPlaced = new Set();
  for (const type of PLACEMENT_ORDER) {
    const wish = lineOf.get(type);
    if (!wish) continue;
    const stage = stages[type];
    const placed = stage ? stage(wish) : null;
    if (placed) {
      entries.push(placed);
      wasPlaced.add(type);
    } else
      drop(
        type,
        counters.length ? 'no-counter-spot-for-it' : 'no-counter-in-room'
      );
  }

  const counterLength = counters.reduce((sum, c) => sum + c.hostW, 0);
  const fits = (list) =>
    list
      .filter((e) => !HUNG.has(e.type))
      .reduce((sum, e) => sum + (e.dimensions?.x || 0), 0) <=
    MAX_COUNTER_SHARE * counterLength;
  // A cut leaned print may yet go on a wall: hold its drop report until then.
  const heldDrops = [];
  const trimDrop = (type, reason) =>
    type === 'staging-art-leaned'
      ? heldDrops.push([type, reason])
      : drop(type, reason);
  trimToBudget(entries, curation, trimDrop, {
    maxProps: MAX_PROPS_PER_KITCHEN,
    fits,
    spends: (e) => !HUNG.has(e.type),
    // The cap is a counter rule; one piece of wall art is outside it.
    counts: (e) => e.type !== 'staging-art',
  });
  // A leaned print the counter had no room for goes on an open wall instead,
  // where it takes no counter and counts toward no cap: stagers prop art on a
  // counter only when the counter has room for it.
  const leanedLine = lineOf.get('staging-art-leaned');
  const hasArt = entries.some((e) => ART_TYPES.has(e.type));
  const hung =
    leanedLine && !hasArt && wasPlaced.has('staging-art-leaned')
      ? hangKitchenArt(leanedLine)
      : null;
  if (hung) entries.push(hung);
  // The print was moved, not lost: report the cut only when no wall took it.
  else for (const [type, reason] of heldDrops) drop(type, reason);
  return entries;
}

module.exports = { planKitchenStaging, PLACEMENT_ORDER };
